import { Injectable } from '@angular/core';

export interface OpportunityNameSource {
  id?: number;
  originalId?: number;
  name: string;
  contactName?: string;
  partnerId?: number | null;
}

export interface OpportunityUpdatePayload {
  id: number;
  name: string;
  partnerId?: number | null;
  [field: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class OpportunityPayloadBuilderService {
  buildOpportunityUpdatePayload(
    source: OpportunityNameSource,
    changes: Record<string, unknown> = {},
  ): OpportunityUpdatePayload {
    const {
      id: _ignoredId,
      name: explicitName,
      partnerId: explicitPartnerId,
      contactName: _ignoredContactName,
      contactPartner: _ignoredContactPartner,
      oportunidad: _ignoredOpportunityAlias,
      originalOpportunityName: _ignoredOriginalNameAlias,
      ...domainChanges
    } = changes;
    const hasExplicitName = Object.prototype.hasOwnProperty.call(changes, 'name');
    const hasExplicitPartner = Object.prototype.hasOwnProperty.call(
      changes,
      'partnerId',
    );
    const partnerId = hasExplicitPartner
      ? (explicitPartnerId as number | null | undefined)
      : source.partnerId;

    return {
      ...domainChanges,
      id: source.originalId ?? source.id ?? 0,
      name:
        hasExplicitName && typeof explicitName === 'string'
          ? explicitName
          : source.name,
      ...(partnerId !== undefined ? { partnerId } : {}),
    };
  }

  /**
   * Construye el payload completo para actualizar una oportunidad
   * Aplica un cambio específico en el path indicado
   */
  buildCompleteOpportunityPayload(opportunity: any, path: string, value: any): any {
    // Construir el payload base con todos los campos necesarios
    const payload: any = {
      leadDescription: opportunity.leadDescription,
      probability: opportunity.probability,
      companyId: opportunity.companyId,
      userId: opportunity.userId,
      stageId: opportunity.stageId,
      teamId: opportunity.teamId,
      projectId: opportunity.projectId ?? opportunity.projects?.[0]?.id,
      costCenterId: opportunity.costCenterId,
      intermediaryCompanyId: opportunity.intermediaryCompanyId,
      expectedRevenue: opportunity.expectedRevenue,
      expectedCloseDate: opportunity.expectedCloseDate,
      leadSource: opportunity.leadSource,
      dateWon: opportunity.dateWon,
      dateLost: opportunity.dateLost,
      lostReasonId: opportunity.lostReasonId,
      lostDescription: opportunity.lostDescription,
      type: opportunity.type,
    };

    // Aplicar el cambio específico del path
    const keys = path.split('.');
    let current = payload;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;

    const partnerId =
      path === 'contactId'
        ? value
        : opportunity.partnerId ?? opportunity.contactId;
    const changes =
      path === 'contactId'
        ? { ...payload, partnerId }
        : { ...payload, ...(partnerId !== undefined ? { partnerId } : {}) };

    return this.buildOpportunityUpdatePayload(
      {
        id: opportunity.id,
        originalId: opportunity.originalId,
        name: opportunity.name,
        partnerId,
      },
      changes,
    );
  }

  /**
   * Construye el payload para actualizar un presupuesto
   */
  buildBudgetPayload(budget: any, changes: any): any {
    return {
      id: budget.id,
      ...changes,
    };
  }

  /**
   * Construye el payload para crear una actividad
   */
  buildCreateActivityPayload(activity: any): any {
    return {
      activityTypeId: activity.activityTypeId,
      parentEntityType: activity.parentEntityType,
      parentEntityId: activity.parentEntityId,
      userId: activity.userId,
      date: activity.date,
      summary: activity.summary,
      note: activity.note,
      dateDeadLine: activity.dateDeadLine,
      state: activity.state,
    };
  }

  /**
   * Construye el payload para actualizar una actividad
   */
  buildUpdateActivityPayload(activity: any): any {
    return {
      id: activity.id,
      activityTypeId: activity.activityTypeId,
      parentEntityType: activity.parentEntityType,
      parentEntityId: activity.parentEntityId,
      userId: activity.userId,
      date: activity.date,
      summary: activity.summary,
      note: activity.note,
      dateDeadLine: activity.dateDeadLine,
      state: activity.state,
      dateDone: activity.dateDone,
    };
  }

  /**
   * Construye el payload para marcar una oportunidad como perdida
   */
  buildMarkAsLostPayload(opportunityId: number, lostReasonId: number, lostDescription: string): any {
    return {
      id: opportunityId,
      lostReasonId: lostReasonId,
      lostDescription: lostDescription,
      dateLost: new Date().toISOString(),
    };
  }

  /**
   * Construye el payload para recuperar una oportunidad perdida
   */
  buildRecoverLeadPayload(opportunityId: number): any {
    return {
      id: opportunityId,
      lostReasonId: null,
      lostDescription: null,
      dateLost: null,
    };
  }

  /**
   * Construye el payload para cambiar la etapa de una oportunidad
   */
  buildChangeStagePayload(opportunity: any, newStageId: number): any {
    return this.buildOpportunityUpdatePayload(
      {
        id: opportunity.id,
        originalId: opportunity.originalId,
        name: opportunity.name,
        partnerId: opportunity.partnerId ?? opportunity.contactId,
      },
      {
        leadDescription: opportunity.leadDescription,
        probability: opportunity.probability,
        companyId: opportunity.companyId,
        userId: opportunity.userId,
        stageId: newStageId,
        teamId: opportunity.teamId,
        costCenterId: opportunity.costCenterId,
        intermediaryCompanyId: opportunity.intermediaryCompanyId,
        expectedRevenue: opportunity.expectedRevenue,
        expectedCloseDate: opportunity.expectedCloseDate,
        leadSource: opportunity.leadSource,
        dateWon: opportunity.dateWon,
        dateLost: opportunity.dateLost,
        lostReasonId: opportunity.lostReasonId,
        lostDescription: opportunity.lostDescription,
        type: opportunity.type,
      },
    );
  }

  /**
   * Construye el payload para convertir un lead en oportunidad
   */
  buildConvertLeadPayload(leadId: number, newType: string = 'Opportunity'): any {
    return {
      id: leadId,
      type: newType,
    };
  }

  /**
   * Construye el payload para crear un proyecto desde una oportunidad
   */
  buildCreateProjectFromOpportunityPayload(opportunity: any, projectData: any): any {
    return {
      opportunityId: opportunity.id,
      name: projectData.name || opportunity.name || opportunity.oportunidad,
      description: projectData.description || opportunity.leadDescription,
      contactId: projectData.contactId || opportunity.contactId,
      companyId: projectData.companyId || opportunity.companyId,
      userId: projectData.userId || opportunity.userId,
      teamId: projectData.teamId || opportunity.teamId,
      ...projectData,
    };
  }
}


